/**
 * @module slideshow-loader
 * Manages video players (Vimeo, YouTube, local) to add functionality
 * like fade-in on play. It dynamically loads the YouTube Iframe API if needed.
 */

// --- YouTube API Loader ---
let youtubeApiReady = false;
let youtubeApiLoading = false;
const youtubeCallbacks = [];

function loadYouTubeApi() {
    if (youtubeApiReady || youtubeApiLoading) return;

    youtubeApiLoading = true;
    const tag = document.createElement('script');
    tag.src = "https://www.youtube.com/iframe_api";
    const firstScriptTag = document.getElementsByTagName('script')[0];
    firstScriptTag.parentNode.insertBefore(tag, firstScriptTag);

    window.onYouTubeIframeAPIReady = () => {
        youtubeApiReady = true;
        youtubeApiLoading = false;
        youtubeCallbacks.forEach(callback => callback());
        youtubeCallbacks.length = 0; // Clear callbacks after execution
    };
}

function onYouTubeApiReady(callback) {
    if (youtubeApiReady) {
        callback();
    } else {
        youtubeCallbacks.push(callback);
        if (!youtubeApiLoading) {
            loadYouTubeApi();
        }
    }
}

/**
 * Sets up an IntersectionObserver to play/pause a video when it enters/leaves the viewport.
 * @param {HTMLElement} element - The element to observe.
 * @param {Function} playFn - The function to call when the element is intersecting.
 * @param {Function} pauseFn - The function to call when the element is not intersecting.
 */
function setupViewportObserver(element, playFn, pauseFn) {
    const observerOptions = {
        root: null, // relative to the viewport
        threshold: 0, // trigger as soon as any part of the element is visible
    };

    const observer = new IntersectionObserver((entries) => {
        entries.forEach(entry => {
            if (entry.isIntersecting) {
                playFn();
            } else {
                pauseFn();
            }
        });
    }, observerOptions);

    observer.observe(element);
}

/**
 * Initializes video players within a given scope, handling hover-to-play based on the
 * `data-display="hover"` attribute of the video container.
 * @param {HTMLElement|Document} [scope=document] - The element to search within for videos.
 */
export function initializeVideoPlayers(scope = document) {
    const videoContainers = scope.querySelectorAll('.video-container');

    videoContainers.forEach(container => {
        const iframe = container.querySelector('iframe');  // Potential iframe video
        const video = container.querySelector('video');    // Potential HTML5 video element
        const isHoverPlay = container.closest('.media').dataset.videoDisplay === 'hover';  // Check for hover play attribute

        // --- Handle Iframe Videos (Vimeo & YouTube) ---
        if (iframe) {
          // --- Vimeo Iframe ---
          if (iframe.src.includes('vimeo.com') && typeof window.Vimeo !== 'undefined') {
              const player = new window.Vimeo.Player(iframe);
              player.on('play', () => iframe.setAttribute('data-playing', 'true'));
              player.on('pause', () => iframe.removeAttribute('data-playing'));

              if (isHoverPlay) {
                  let pauseTimeout;
                  container.addEventListener('mouseenter', () => {
                      clearTimeout(pauseTimeout);
                      player.play();
                  });
                  container.addEventListener('mouseleave', () => {
                      iframe.removeAttribute('data-playing');
                      pauseTimeout = setTimeout(() => player.pause(), 1000);
                  });
                  player.pause();
                  iframe.removeAttribute('data-playing');
              } else {
                  // Play/pause based on viewport visibility
                  player.setVolume(0); // Mute for autoplay-like behavior
                  setupViewportObserver(container, () => player.play(), () => player.pause());
              }
          }
          // --- YouTube Iframe ---
          else if (iframe.src.includes('youtube.com')) {
              onYouTubeApiReady(() => {
                  const player = new window.YT.Player(iframe, {
                      events: {
                          'onReady': (event) => {
                              event.target.mute();
                              if (isHoverPlay) {
                                  let pauseTimeout;
                                  container.addEventListener('mouseenter', () => {
                                      clearTimeout(pauseTimeout);
                                      event.target.playVideo();
                                  });
                                  container.addEventListener('mouseleave', () => {
                                      iframe.removeAttribute('data-playing');
                                      pauseTimeout = setTimeout(() => event.target.pauseVideo(), 1000);
                                  });
                              } else {
                                  // Play/pause based on viewport visibility
                                  setupViewportObserver(container, () => event.target.playVideo(), () => event.target.pauseVideo());
                              }
                          },
                          'onStateChange': (event) => {
                              if (event.data === window.YT.PlayerState.PLAYING) {
                                  iframe.setAttribute('data-playing', 'true');
                              } else {
                                  iframe.removeAttribute('data-playing');
                              }
                          }
                      }
                  });
              });
          }
      }

      // --- Handle HTML5 <video> Element ---
        if (video) {
          if (isHoverPlay) {
  
              container.addEventListener('mouseenter', () => {
                  clearTimeout(pauseTimeout);
                  video.play();
              });
  
              container.addEventListener('mouseleave', () => {
                  video.removeAttribute('data-playing');
                  pauseTimeout = setTimeout(() => {
                      video.pause();
                  }, 1000);
              });
  
              // Initially pause and ensure not marked as playing for hover-to-play
              video.pause();
              video.removeAttribute('data-playing');
          }
        }
    });
}
